/*  Shared  */
import { allReportsConfig } from 'application/Home/Reports/internal/allReportsConfig';
import { Route } from 'application/Shared/Helpers';
import {
    PeriodOptions,
    ReportConfig,
    ReportConfigCheckboxes,
    ReportParams,
    SubSubReportsList
} from 'application/Shared/types/reports';

/* NPM */
import {
    endOfDay,
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfWeek
} from 'date-fns';
import { TFunction } from 'i18next';

// A list of reports that are in the v2 reports.
// We can use this as we redesign/refactor the reports.
const V2_REPORTS = ['applicationOverlap'];

// We need to be lowercase. We have them as camelCase in the backend,
// but the getReportType function in ChooseReportComponent.tsx
// returns the tab name, which is CapitalizedCamelCase.
const v2Reports = V2_REPORTS.map((report) => report.toLowerCase());

// Also need to convert the incoming reportType to lowercase.
const checkIfV2Report = (reportType?: string) =>
    v2Reports.includes(reportType?.toLowerCase() ?? '');

export const isV2Report = (reportType?: string) => checkIfV2Report(reportType);

export const getStartOfDay = (eventDateObject?: Date | string | number) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    return new Date(startOfDay(date)).getTime();
};

export const getEndOfDay = (eventDateObject?: Date | string | number) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    return new Date(endOfDay(date)).getTime();
};

export const getStartOfMonth = (eventDateObject?: Date | string | number) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    return new Date(startOfMonth(date)).getTime();
};

export const getEndOfMonth = (eventDateObject?: Date | string | number) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    const lastDay = new Date(endOfMonth(date));
    const today = new Date();
    return lastDay.getTime() > today.getTime()
        ? today.getTime()
        : lastDay.getTime();
};

export const getStartOfQuarter = (eventDateObject?: Date | string | number) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    return new Date(startOfQuarter(date)).getTime();
};

export const getEndOfQuarter = (eventDateObject?: Date | string | number) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    return new Date(endOfQuarter(date)).getTime();
};

export const getStartOfWeek = (eventDateObject?: Date | string | number) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    return new Date(startOfWeek(date)).getTime();
};

export const getEndOfWeek = (eventDateObject?: Date | string | number) => {
    const date = eventDateObject ? new Date(eventDateObject) : new Date();
    const lastDay = new Date(endOfWeek(date));
    const today = new Date();
    return lastDay.getTime() > today.getTime()
        ? today.getTime()
        : lastDay.getTime();
};

export const getDefaultReportConfiguration = ({
    reportType,
    clientId
}: {
    clientId?: string | null;
    reportType: string;
}) => {
    const applicationOrCategorySelection =
        reportType === 'applicationOverlap' ? 'category' : 'application';
    const disablePersonal = reportType !== 'applicationOverlap';
    const userOrGroupSelection = reportType === 'offboard' ? 'users' : 'all';

    const startTime = getStartOfMonth();
    const endTime = getEndOfMonth();

    const reportSelections = buildDefaultReportSelections({ type: reportType });

    const params: ReportParams = {
        applicationOrCategorySelection,
        applicationSelection: ['0'],
        breachSelection: ['0'],
        businessOwnerSelection: null,
        categorySelection: ['0'],
        clientSelection: ['0'],
        disableBrowsers: true,
        disableInactive: true,
        disablePersonal,
        disableUnclassified: false,
        discoveryOverviewMatrixTags: ['0'],
        endTime,
        excludeDormant: false,
        excludePersonalAccounts: false,
        groupSelection: ['0'],
        includeInactive: false,
        includeUncataloged: true,
        includeUntagged: true,
        managementStageSelection: ['0'],
        period: PeriodOptions.period,
        platformSelection: ['0'],
        reportSelections,
        startTime,
        statusSelection: ['0'],
        subCategorySelection: ['0'],
        tagSelection: ['0'],
        type: reportType,
        userOrGroupSelection,
        userSelection: ['0']
    };

    if (clientId) {
        params.platformTenantId = clientId;
    }

    return params;
};

/**
 * Generates a URL for running a report with the specified parameters
 * @param clientId - Optional ID of the client to run the report for
 * @param partnerId - Optional ID of the partner to run the report for
 * @param reportParams - Partial report parameters to override defaults
 * @returns URL string for running the report with the specified configuration
 */
export const runReportLinkURL = ({
    clientId,
    partnerId,
    reportParams
}: {
    clientId?: string;
    partnerId?: string;
    reportParams: Partial<ReportParams>;
}) => {
    const route = Route.buildBaseReportsRoute({ clientId, partnerId });
    const params = {
        ...getDefaultReportConfiguration({
            clientId,
            reportType: reportParams.type!
        }),
        ...reportParams
    };

    // We need to update userOrGroupSelection and applicationOrCategorySelection because it is unlikely for the developer
    // to have added these options in RunReportLink
    // And why would we? - they feel like obscure params, yet they go hand in hand,
    // most likely we just set the application(s), user(s), platform(s), etc., but not these two.

    // userOrGroupSelection:
    // We need to set it to 'users' or 'groups' depending on the userSelection or groupSelection.
    // If both are empty or default values then we set it to 'all'.
    if (
        (!params.userSelection?.length &&
            !params.groupSelection?.length &&
            !params.userOrGroupSelection) ||
        (params.userSelection?.length &&
            params.userSelection[0] === '0' &&
            params.groupSelection?.length &&
            params.groupSelection[0] === '0')
    ) {
        params.userOrGroupSelection = 'all';
    }
    if (params.groupSelection?.length && params.groupSelection[0] !== '0') {
        params.userOrGroupSelection = 'groups';
    }
    if (
        (params.userSelection?.length && params.userSelection[0] !== '0') ||
        params.type === 'offboard'
    ) {
        params.userOrGroupSelection = 'users';
    }

    // applicationOrCategorySelection:
    // applicationSelection goes first because with platforms we can select applications, therefore applicationSelection[0] !== '0', yet
    // applicationOrCategorySelection needs to be 'platform'
    if (
        params.applicationSelection?.length &&
        params.applicationSelection[0] !== '0'
    ) {
        params.applicationOrCategorySelection = 'application';
    }

    if (
        (params.categorySelection?.length &&
            params.categorySelection[0] !== '0') ||
        params.type === 'applicationOverlap'
    ) {
        params.applicationOrCategorySelection = 'category';
    }
    if (
        params.subCategorySelection?.length &&
        params.subCategorySelection[0] !== '0'
    ) {
        params.applicationOrCategorySelection = 'subCategory';
    }
    if (
        params.managementStageSelection?.length &&
        params.managementStageSelection[0] !== '0'
    ) {
        params.applicationOrCategorySelection = 'managementStage';
    }
    if (
        params.platformSelection?.length &&
        params.platformSelection[0] !== '0'
    ) {
        params.applicationOrCategorySelection = 'platform';
    }
    if (params.statusSelection?.length && params.statusSelection[0] !== '0') {
        params.applicationOrCategorySelection = 'status';
    }
    if (params.tagSelection?.length && params.tagSelection[0] !== '0') {
        params.applicationOrCategorySelection = 'tag';
    }

    return `${route}/${reportParams.type}/summary?reportParams=${JSON.stringify(
        params
    )}`;
};

/**
 * Builds default report selections based on report configuration and type
 * @param type - The type of report to build selections for
 * @returns Array of report selection strings representing the default selections
 */
export const buildDefaultReportSelections = ({ type }: { type: string }) => {
    const reportSelections = [];

    // allReportsConfig is an object with information about all the (~19) reports
    // we can generate at both the admin and partner level.
    // Each item in the object has some other properties that are important here for initializing the report selections:
    // `type`, a string, which is basically the name of the report, e.g., "discovery".
    // `checkboxes`, an array of objects where each key has properties:
    //     `reportEndpoint`, e.g., "discovery/"
    //     `type`, e.g., "discovery"
    //     `subOptions`, an array of object that describes the child checkboxes under the main checkboxes.
    //           e.g., "[{ key: 'Overview' }", { key: 'RiskAnalysis' }, etc.]

    // We first start isolating the only entry that matches the report we want to run
    const selectedReport: ReportConfig | undefined = allReportsConfig.find(
        (singleReport) => singleReport.type === type
    );

    // If there is such report, then get the original reportSelections by
    // getting all the keys, from the parent, to the subkey and, if available, the sub of the subkey.
    // For example, the report type discovery (parent) has a Discovery section (subkey), that in turn has subsections (subSubKeys): overview, risk analysis, overview breakdown, and app breakdown
    // Final reportSelections will look like
    // ['discovery', 'discoveryDiscovery', 'discoveryDiscovery','discoveryDiscoveryOverview', 'discoveryDiscoveryRiskAnalysys','discoveryDiscoveryOverviewBreakdown', 'discoveryDiscoveryApplicationBreakdown']
    if (selectedReport) {
        const parentType = selectedReport.type;
        reportSelections.push(parentType);

        selectedReport.checkboxes.forEach((checkboxItem) => {
            const subkey = parentType + checkboxItem.type;
            reportSelections.push(subkey);

            // These subkeys will have subOptions, e.g., "discoveryDiscovery" will have Overview, Risk Analysis, etc.
            // We need to add them to the reportSelections array.
            // We use the default value for the subOption, if available or not false, we add it ot the reportSelections.
            Object.values(checkboxItem.subOptions).forEach((subOption) => {
                if (!subOption.hidden && subOption?.default !== false) {
                    const subSubKey =
                        parentType + checkboxItem.type + subOption.key;
                    reportSelections.push(subSubKey);
                }
            });
        });
    }

    return reportSelections;
};

/**
 * Builds a selection of sub-reports based on the report selections and type
 * @param reportSelections - Array of selected report identifiers
 * @param type - The type of report to build sub-report selections for
 * @returns Array of ReportConfigCheckboxes representing the selected sub-reports, or null if report type not found
 */
export const buildSubReportsSelection = ({
    reportSelections,
    type
}: {
    reportSelections: string[];
    type: string;
}) => {
    // Find the report configuration matching the specified type
    const report = allReportsConfig.find(
        (singleConfigReport) => singleConfigReport.type === type
    );

    if (report == null) {
        return [];
    }

    const { checkboxes } = report;

    const subReports: ReportConfigCheckboxes[] = [];
    Object.values(checkboxes).forEach((checkbox) => {
        // It is just top level, say for organizations, we get usage, library, and or category, not the subreports of those
        if (reportSelections?.includes(report.type + checkbox.type)) {
            subReports.push(checkbox);
        }
    });

    return subReports;
};

/**
 * Builds a detailed selection of sub-sub-reports based on the report selections and type.
 * This function drills down to the lowest level of report hierarchy to get the specific
 * sub-options selected under each sub-report section.
 *
 * @param reportSelections - Array of selected report identifiers that determine which sub-sub-reports to include
 * @param type - The type of report to build sub-sub-report selections for
 * @returns Array of objects containing the sub-report type and its selected sub-sub-reports, or null if report type not found
 */
export const buildSubSubReportsSelection = ({
    reportSelections,
    type
}: {
    reportSelections: string[];
    type: string;
}) => {
    // Find the report configuration matching the specified type
    const report = allReportsConfig.find(
        (singleConfigReport) => singleConfigReport.type === type
    );

    if (report == null) {
        return null;
    }

    const { checkboxes } = report;

    const subSubReportsList: SubSubReportsList[] = [];

    Object.values(checkboxes).forEach((checkbox) => {
        // It is just top level, say for organizations, we get usage, library, and or category, not the subreports of those
        if (reportSelections?.includes(report.type + checkbox.type)) {
            const subSubOptions: string[] = [];
            checkbox.subOptions.forEach((subOption) => {
                if (
                    reportSelections?.includes(
                        report.type + checkbox.type + subOption.key
                    )
                ) {
                    subSubOptions.push(subOption.key);
                }
            });
            subSubReportsList.push({
                subSubReports: subSubOptions,
                type: checkbox.type
            });
        }
    });

    return subSubReportsList;
};

/**
 * Gets a report configuration by its type from allReportsConfig
 * @param reportType - The type of report to get configuration for
 * @returns The report configuration or null if not found
 */
export const getReportConfigByType = (
    reportType: string
): ReportConfig | null =>
    allReportsConfig.find((config) => config.type === reportType) || null;

/**
 * Gets the selected sub-report options for a given report type and returns them grouped by type
 * @param reportSelections - Array of report selection keys (e.g., ["discoveryDiscoveryOverview", "discoveryDiscoveryRiskAnalysis"])
 * @param reportType - The type of report (e.g., "discovery", "qbr")
 * @param t - Translation function for converting keys to human-readable text
 * @returns Array of objects with type label and selected sub-options (e.g., [{ type: "Discovery", options: ["Overview", "Risk Analysis"] }])
 */
export const getSelectedSubReportOptions = ({
    reportSelections,
    type: reportType,
    t
}: {
    reportSelections: string[];
    type: string;
    t: TFunction<'translation', undefined>;
}): Array<{ options: string[]; type: string }> => {
    const config = getReportConfigByType(reportType);
    if (!config) return [];

    const groupedSelectedOptions: Array<{ options: string[]; type: string }> =
        config.checkboxes.reduce((acc, checkbox) => {
            const selectedOptions = checkbox.subOptions.reduce(
                (acc, subOption) => {
                    const fullKey = reportType + checkbox.type + subOption.key;
                    if (reportSelections.includes(fullKey)) {
                        acc.push(
                            t(
                                `Reports.${reportType}.checkbox.${checkbox.type}.subOption.${subOption.key}`
                            )
                        );
                    }
                    return acc;
                },
                [] as string[]
            );

            if (selectedOptions.length > 0) {
                acc.push({
                    options: selectedOptions,
                    type: t(`Reports.${reportType}.checkbox.${checkbox.type}`)
                });
            }
            return acc;
        }, [] as Array<{ options: string[]; type: string }>);

    return groupedSelectedOptions;
};

/**
 * Gets the selected advanced report options and returns them as translated labels
 * @param reportConfig - The report configuration containing advanced options setup
 * @param params - The report parameters containing user selections
 * @param t - Translation function for converting keys to human-readable text
 * @returns Array of translated advanced option labels (e.g., ["Remove Browser", "Remove Inactive"])
 */
export const getSelectedAdvancedOptions = ({
    reportConfig,
    params,
    t
}: {
    reportConfig: ReportConfig | null;
    params: ReportParams;
    t: TFunction<'translation', undefined>;
}): string[] => {
    if (!reportConfig) return [];
    const advancedOptions = reportConfig.setup.advancedReportSelectionOptions;
    if (!advancedOptions) return [];

    return Object.entries(advancedOptions)
        .filter(
            ([key, isEnabled]) =>
                isEnabled &&
                // Remove "Selection" suffix from key to match ReportParams property names
                // e.g., "includeSelection" becomes "include" to match params.include
                params[key.replace('Selection', '') as keyof ReportParams]
        )
        .map(([key]) =>
            // Remove "Selection" suffix from key to match translation key format
            // e.g., "includeSelection" becomes "include" for Reports.advancedOptions.include
            t(`Reports.advancedOptions.${key.replace('Selection', '')}`)
        );
};

// Helper function to count navigation sections
export const countNavigationSections = (reportType?: string) => {
    if (!reportType) return 0;

    // Find the report config for the current report type
    const reportConfig = allReportsConfig.find(
        (config) => config.type === reportType
    );
    if (!reportConfig) return 0;

    // Count subOptions from all checkboxes in the report config
    let totalSubOptions = 0;
    reportConfig.checkboxes.forEach((checkbox) => {
        totalSubOptions += checkbox.subOptions.length;
    });

    return totalSubOptions;
};

export const hasMultipleSections = (reportType?: string) =>
    countNavigationSections(reportType) > 1;

export const isAdminReportAtPartnerLevel = ({
    clientId,
    reportType
}: {
    clientId: string | null | undefined;
    reportType: string;
}) => {
    // Get the original report config
    const reportConfig = getReportConfigByType(reportType!);

    if (!reportConfig) {
        return null;
    }

    return !!reportConfig?.setup?.canBeShownAtPartnerLevel && !clientId;
};
